// OIDC PKCE flow against EPFL Entra (Azure AD).
//
// Architecture:
// - SPA flow with PKCE (no client secret).
// - The platform-specific redirect step is delegated to an AuthDriver
//   (see authDriver.ts): extension → chrome.identity.launchWebAuthFlow;
//   web → full-page redirect to /auth/callback. Everything else here is
//   shared verbatim by both targets.
// - The token sent to the FastAPI backend is the **id_token** (not the
//   access_token, which is meant for Microsoft Graph because of User.Read).
//   The backend validates signature + iss + aud + exp against EPFL JWKS.
// - Session (id/refresh/expiry/account) is persisted in IndexedDB via the
//   `settings` store, alongside the rest of the app config.
// - getAuthToken() returns the id_token (refreshing transparently if expired).
//   Three-tier renewal, so the user stays signed in for weeks rather than a day:
//     1. refresh_token → new id_token (works for the token's 24h lifetime);
//     2. once that dies (Entra hard-caps SPA refresh tokens at 24h), a
//        prompt=none authorize round-trip in a hidden iframe mints a new pair
//        straight off the Entra browser session, with no UI (MSAL's ssoSilent);
//     3. when the iframe can't (third-party cookies blocked — the Chrome
//        setting privacy-minded users have), the same prompt=none request as
//        a top-level navigation of the tab, at startup (silentLoginRedirect).
//   The interactive login only comes back when Entra itself wants the user.

import { getSetting, setSetting, deleteSetting } from "../db/settings";
import { authDriver } from "./authDriver";

const TENANT_ID = "f6c2556a-c4fb-4ab1-a2c7-9e220df11c43";
const CLIENT_ID = "fe9b7ccb-d941-4853-bd4c-eb9487d29032";
const AUTHORITY = `https://login.microsoftonline.com/${TENANT_ID}`;
const AUTHORIZE_URL = `${AUTHORITY}/oauth2/v2.0/authorize`;
const TOKEN_URL = `${AUTHORITY}/oauth2/v2.0/token`;
// openid/profile/email give id_token claims; offline_access yields a
// refresh_token. User.Read is unused by us but kept because the EPFL portal
// pre-checks it and we want the registration to match what we request.
const SCOPES = "openid profile email offline_access User.Read";

const SESSION_KEY = "oidc.session";
// Last account we successfully signed in as. Survives an *expired* session on
// purpose: it is the login_hint that lets silentLogin() renew without any UI.
// Cleared only on an explicit logout, so signing out really signs you out.
const LAST_ACCOUNT_KEY = "oidc.lastAccount";
// Outcome of the last silent renewal, for diagnosis only (shown in the About
// panel). silentLogin() swallows its errors by design, so without this a daily
// re-login gives no clue as to why the renewal didn't work.
const LAST_SILENT_KEY = "oidc.lastSilent";
// sessionStorage key holding {verifier,state} between beginLogin() and
// finishLogin(). sessionStorage (not IndexedDB) because it survives the
// same-tab navigation of the web redirect flow yet is scoped to this tab,
// and is cleared the moment the exchange completes.
const PKCE_KEY = "oidc.pkce";
// sessionStorage key: when this tab last sent itself to /authorize?prompt=none
// (silentLoginRedirect). Stops a renewal that comes back empty-handed from
// bouncing the tab to Microsoft again and again.
const REDIRECT_GUARD_KEY = "oidc.silentRedirectAt";
const REDIRECT_GUARD_MS = 5 * 60_000;
// Refresh a bit before actual expiry to avoid races where the token expires
// in flight between the check and the server-side validation.
const EXPIRY_SKEW_MS = 60_000;

export interface OidcAccount {
  email: string;
  name: string;
}

export interface OidcSession {
  idToken: string;
  refreshToken: string;
  expiresAt: number;
  account: OidcAccount;
}

export class OidcError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "OidcError";
  }
}

/**
 * The stored refresh token is dead: no further token call can use it. Causes,
 * same outcome: Entra caps SPA refresh tokens at a hard 24h lifetime
 * (AADSTS700084, non-extensible — it's a deliberate SPA constraint, not a bug),
 * it returns invalid_grant when the user's consent is revoked, and
 * interaction_required when conditional access wants a fresh sign-in. Callers
 * treat this distinctly from transient refresh failures: purge the session, try
 * a silent renewal off the Entra browser session (silentLogin), and only prompt
 * a re-login if that fails too — never surface a raw OAuth error.
 */
export class SessionExpiredError extends OidcError {
  constructor() {
    super("Session EPFL expirée. Reconnectez-vous pour continuer.");
    this.name = "SessionExpiredError";
  }
}

function getRedirectUri(): string {
  return authDriver.redirectUri();
}

function base64UrlEncode(bytes: Uint8Array): string {
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function randomVerifier(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

async function challengeFromVerifier(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return base64UrlEncode(new Uint8Array(digest));
}

function decodeJwtClaims(token: string): Record<string, unknown> {
  // We only read claims for display purposes (email/name). Signature is
  // validated server-side, never here.
  const parts = token.split(".");
  if (parts.length < 2) throw new OidcError("Malformed JWT");
  const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
  const padded = payload + "=".repeat((4 - (payload.length % 4)) % 4);
  // atob yields one char per byte: decode as UTF-8 or "Jérôme" reads "JÃ©rÃ´me".
  const bytes = Uint8Array.from(atob(padded), (ch) => ch.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes));
}

function accountFromIdToken(idToken: string): OidcAccount {
  const c = decodeJwtClaims(idToken);
  return {
    email: (c.email as string) ?? (c.preferred_username as string) ?? "",
    // `name` is sometimes absent or empty; given_name/family_name usually are not.
    name:
      (c.name as string) ||
      [c.given_name, c.family_name].filter(Boolean).join(" "),
  };
}

interface TokenResponse {
  id_token: string;
  refresh_token?: string;
  expires_in: number;
}

async function exchangeCodeForTokens(
  code: string,
  verifier: string,
): Promise<OidcSession> {
  const body = new URLSearchParams({
    client_id: CLIENT_ID,
    grant_type: "authorization_code",
    code,
    redirect_uri: getRedirectUri(),
    code_verifier: verifier,
    scope: SCOPES,
  });
  const res = await fetch(TOKEN_URL, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => "");
    throw new OidcError(`Token exchange failed: ${res.status} ${detail}`);
  }
  const data = (await res.json()) as TokenResponse;
  if (!data.refresh_token) {
    throw new OidcError(
      "No refresh_token returned (offline_access scope was likely refused).",
    );
  }
  return {
    idToken: data.id_token,
    refreshToken: data.refresh_token,
    expiresAt: Date.now() + data.expires_in * 1000,
    account: accountFromIdToken(data.id_token),
  };
}

async function refreshTokens(refreshToken: string): Promise<OidcSession> {
  const body = new URLSearchParams({
    client_id: CLIENT_ID,
    grant_type: "refresh_token",
    refresh_token: refreshToken,
    scope: SCOPES,
  });
  const res = await fetch(TOKEN_URL, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => "");
    // A 4xx on a refresh_token grant means the token is no longer redeemable —
    // unrecoverable without a new sign-in. invalid_grant is the usual one (Entra's
    // 24h SPA cap, revoked consent), but Entra also answers interaction_required
    // (conditional access, MFA/sign-in frequency) with a 400. Matching only
    // invalid_grant left such a session stored and dead: every later call failed
    // the same way and the silent renewal below never got its chance. 429 is
    // throttling, and network errors / 5xx are transient: none of those may log
    // the user out, so they stay a generic OidcError the caller can retry.
    if (res.status >= 400 && res.status < 500 && res.status !== 429) {
      console.warn("[oidc] refresh token rejected:", res.status, detail.slice(0, 300));
      throw new SessionExpiredError();
    }
    throw new OidcError(`Token refresh failed: ${res.status} ${detail}`);
  }
  const data = (await res.json()) as TokenResponse;
  return {
    idToken: data.id_token,
    // Some IdPs rotate the refresh token, others don't. Keep the new one if
    // provided, fall back to the old one otherwise.
    refreshToken: data.refresh_token ?? refreshToken,
    expiresAt: Date.now() + data.expires_in * 1000,
    account: accountFromIdToken(data.id_token),
  };
}

// Guards the one-shot hint backfill below.
let hintBackfilled = false;

export async function getCurrentSession(): Promise<OidcSession | null> {
  const v = await getSetting<OidcSession>(SESSION_KEY);
  // Sessions created before the silent-renewal hint existed have no
  // LAST_ACCOUNT_KEY. Backfill it once so those users get silent renewals from
  // the first run after the update instead of one last forced re-login.
  if (v && !hintBackfilled) {
    hintBackfilled = true;
    if (!(await lastAccount())) await setSetting(LAST_ACCOUNT_KEY, v.account);
  }
  return v ?? null;
}

type SessionListener = (session: OidcSession | null) => void;
const sessionListeners = new Set<SessionListener>();

/**
 * Observe session changes. Needed because the session can now be renewed deep
 * inside a chat turn (silentLogin) or dropped there — the UI badge would
 * otherwise keep showing whatever it read at startup. Returns an unsubscribe fn.
 */
export function onSessionChange(listener: SessionListener): () => void {
  sessionListeners.add(listener);
  return () => sessionListeners.delete(listener);
}

function notifySessionChanged(session: OidcSession | null): void {
  for (const l of sessionListeners) l(session);
}

async function saveSession(session: OidcSession): Promise<void> {
  await setSetting(SESSION_KEY, session);
  await setSetting(LAST_ACCOUNT_KEY, session.account);
  notifySessionChanged(session);
}

/** Drop the stored session but KEEP the account hint, so the next call can try
 *  a silent renewal. Used when the refresh token dies (Entra's 24h SPA cap).
 *  Deliberately does NOT notify: the silent renewal that follows usually puts a
 *  session right back, and announcing the gap would flash a sign-in screen
 *  mid-chat. Callers notify themselves once the renewal has actually failed. */
async function dropSession(): Promise<void> {
  await deleteSetting(SESSION_KEY);
}

/** The account we were last signed in as, if any — the silent-renewal hint. */
async function lastAccount(): Promise<OidcAccount | null> {
  return (await getSetting<OidcAccount>(LAST_ACCOUNT_KEY)) ?? null;
}

/** True when a silent renewal is worth attempting (we know who to renew for). */
export async function hasSilentHint(): Promise<boolean> {
  return (await lastAccount()) !== null;
}

interface PendingPkce {
  verifier: string;
  state: string;
  /** Set when the flow is a top-level prompt=none renewal (silentLoginRedirect),
   *  so finishLogin() can record its outcome and lift the loop guard. */
  silentRedirect?: boolean;
}

/**
 * Build an /authorize URL and stash the matching PKCE secrets.
 *
 * The secrets go to sessionStorage before we leave for Entra: on the web target
 * the tab navigates away, so they must survive the round-trip; on the extension
 * target start() resolves inline and finishLogin() reads them right after.
 */
async function prepareAuthUrl(
  extra: Record<string, string>,
  silentRedirect = false,
): Promise<string> {
  const verifier = randomVerifier();
  const challenge = await challengeFromVerifier(verifier);
  const state = randomVerifier();

  const pending: PendingPkce = { verifier, state, silentRedirect };
  sessionStorage.setItem(PKCE_KEY, JSON.stringify(pending));

  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    response_type: "code",
    redirect_uri: getRedirectUri(),
    response_mode: "query",
    scope: SCOPES,
    code_challenge: challenge,
    code_challenge_method: "S256",
    state,
    ...extra,
  });
  return `${AUTHORIZE_URL}?${params.toString()}`;
}

export async function loginEpfl(): Promise<OidcSession> {
  const authUrl = await prepareAuthUrl({
    // prompt=select_account lets the user pick a different EPFL identity if
    // they happen to be signed in to several Microsoft accounts in the same
    // browser profile. Mild UX win, no correctness impact.
    prompt: "select_account",
  });

  // Extension: resolves with the callback URL → finish inline.
  // Web: navigates away and never resolves → finishLogin() runs on /auth/callback.
  const redirectUrl = await authDriver.start(authUrl);
  return finishLogin(redirectUrl);
}

export interface SilentOutcome {
  at: number;
  ok: boolean;
  reason: string;
}

async function recordSilent(ok: boolean, reason: string): Promise<void> {
  const outcome: SilentOutcome = { at: Date.now(), ok, reason: reason.slice(0, 300) };
  if (!ok) console.warn("[oidc] silent renewal failed:", outcome.reason);
  try {
    await setSetting(LAST_SILENT_KEY, outcome);
  } catch {
    /* diagnostic only: never let it break a renewal */
  }
}

export async function getLastSilentOutcome(): Promise<SilentOutcome | null> {
  return (await getSetting<SilentOutcome>(LAST_SILENT_KEY)) ?? null;
}

// One silent renewal at a time: several chat calls hit an expired session at
// once, and each extra prompt=none round-trip would burn its own PKCE stash
// (they share one sessionStorage slot) and race the others.
let inFlightSilent: Promise<OidcSession | null> | null = null;

/**
 * Re-establish a session with NO user interaction, using the Entra browser
 * session (SSO cookie) rather than our own refresh token.
 *
 * This is what keeps the user signed in across days. Entra caps SPA refresh
 * tokens at a hard, non-extensible 24h, so the token-refresh path alone
 * guarantees a re-login every day. The Entra *session* behind it lives far
 * longer (typically 90 days rolling with "stay signed in"), and a prompt=none
 * authorize request mints a brand-new 24h refresh token off it — invisibly.
 * Same mechanism as MSAL's ssoSilent, which is why the Outlook add-in never
 * asks the user to reconnect.
 *
 * Returns null (never throws) when Entra would need to show something: no
 * browser session, MFA/consent due, or a target that can't renew silently.
 * The caller then falls back to the interactive login. The reason is kept in
 * getLastSilentOutcome().
 */
export async function silentLogin(): Promise<OidcSession | null> {
  if (inFlightSilent) return inFlightSilent;

  const run = (async (): Promise<OidcSession | null> => {
    const hint = await lastAccount();
    // Without a hint there is nobody to renew for — a prompt=none request would
    // just bounce, and on a multi-account profile it could silently pick the
    // wrong identity.
    if (!hint) return null;
    try {
      const authUrl = await prepareAuthUrl({
        prompt: "none",
        login_hint: hint.email,
      });
      const redirectUrl = await authDriver.startSilent(authUrl);
      const session = await finishLogin(redirectUrl);
      await recordSilent(true, "ok");
      return session;
    } catch (err) {
      // login_required / interaction_required / consent_required all land here
      // via finishLogin's error branch, as do a driver that got no answer and a
      // failed token exchange — all mean "ask the user". Keep the reason.
      await recordSilent(false, err instanceof Error ? err.message : String(err));
      return null;
    } finally {
      // Nothing may reuse a half-spent PKCE stash.
      sessionStorage.removeItem(PKCE_KEY);
    }
  })();

  inFlightSilent = run.finally(() => {
    inFlightSilent = null;
  });
  return inFlightSilent;
}

/**
 * Last resort before asking the user, web target only: send the TAB itself to
 * /authorize?prompt=none — a sign-in with no screen when Entra can answer from
 * its session (it comes straight back with a code, about a second).
 *
 * Why it exists: the hidden-iframe renewal (silentLogin) needs Microsoft's
 * cookies in a third-party context, and Chrome's "Block third-party cookies"
 * (plus every Safari/Firefox default) withholds them — Entra then sees no
 * session and answers login_required every morning, although the session is
 * alive and the very same request in a tab proves it. A navigation is
 * first-party, so it works wherever the sign-in itself worked. Same ladder as
 * MSAL: ssoSilent, then redirect.
 *
 * Only for moments when nothing is in flight (startup): the page unloads.
 * Returns true when the navigation was started, false when there is nobody to
 * renew for, the target has no page to navigate (extension), or this tab
 * already tried in the last few minutes and came back without a session — the
 * guard against a redirect loop. The outcome lands in getLastSilentOutcome()
 * through finishLogin() on the way back.
 */
export async function silentLoginRedirect(): Promise<boolean> {
  const hint = await lastAccount();
  if (!hint) return false;
  const last = Number(sessionStorage.getItem(REDIRECT_GUARD_KEY) ?? 0);
  if (Date.now() - last < REDIRECT_GUARD_MS) return false;
  const authUrl = await prepareAuthUrl({ prompt: "none", login_hint: hint.email }, true);
  sessionStorage.setItem(REDIRECT_GUARD_KEY, String(Date.now()));
  if (authDriver.silentRedirect(authUrl)) return true;
  sessionStorage.removeItem(REDIRECT_GUARD_KEY);
  sessionStorage.removeItem(PKCE_KEY);
  return false;
}

/**
 * Complete the flow from the Entra callback URL: validate state, exchange the
 * code, persist the session. Called inline by loginEpfl() on the extension
 * target, and by the /auth/callback page on the web target. Shared verbatim so
 * the CSRF check and token exchange live in exactly one place.
 */
export async function finishLogin(redirectUrl: string): Promise<OidcSession> {
  const raw = sessionStorage.getItem(PKCE_KEY);
  if (!raw) {
    throw new OidcError("Login state missing (no flow started in this tab).");
  }
  sessionStorage.removeItem(PKCE_KEY);
  const { verifier, state, silentRedirect } = JSON.parse(raw) as PendingPkce;

  try {
    const url = new URL(redirectUrl);
    const returnedState = url.searchParams.get("state");
    if (returnedState !== state) {
      throw new OidcError("State mismatch (possible CSRF). Login aborted.");
    }
    const code = url.searchParams.get("code");
    if (!code) {
      const err = url.searchParams.get("error_description") ?? "no code";
      throw new OidcError(`Authorization error: ${err}`);
    }

    const session = await exchangeCodeForTokens(code, verifier);
    await saveSession(session);
    if (silentRedirect) {
      sessionStorage.removeItem(REDIRECT_GUARD_KEY);
      await recordSilent(true, "ok (redirect)");
    }
    return session;
  } catch (err) {
    // A top-level renewal has no caller left to report to (the page was
    // reloaded): keep its reason where the iframe path keeps its own.
    if (silentRedirect) {
      await recordSilent(false, `redirect: ${err instanceof Error ? err.message : String(err)}`);
    }
    throw err;
  }
}

export async function logoutEpfl(): Promise<void> {
  await deleteSetting(SESSION_KEY);
  // Also drop the silent-renewal hint: an explicit sign-out must not be undone
  // by the next call quietly signing the same account back in.
  await deleteSetting(LAST_ACCOUNT_KEY);
  notifySessionChanged(null);
}

// One shared in-flight refresh, coalescing concurrent callers. Entra SPA
// refresh tokens are single-use (rotated on every redemption), so two parallel
// refreshes race: the first rotates the token, the second redeems an
// already-invalidated one, fails, and logs the user out. The chat fires several
// LLM calls at once, so this hit reliably right at the 1h id_token expiry.
let inFlightRefresh: Promise<OidcSession> | null = null;

export async function getAuthToken(): Promise<string> {
  const session = await getCurrentSession();
  if (!session) {
    // No stored session — but the Entra browser session may still be alive
    // (typically it is: ours died at the 24h SPA cap, theirs lasts weeks).
    // Renew silently before declaring the user logged out.
    const revived = await silentLogin();
    if (revived) return revived.idToken;
    throw new OidcError("Not signed in to EPFL.");
  }

  if (session.expiresAt - EXPIRY_SKEW_MS > Date.now()) {
    return session.idToken;
  }

  // Expired (or about to). Refresh transparently, but only once: concurrent
  // callers await the same refresh so the single-use refresh_token is redeemed
  // exactly once.
  if (!inFlightRefresh) {
    inFlightRefresh = refreshTokens(session.refreshToken)
      .then(async (fresh) => {
        await saveSession(fresh);
        return fresh;
      })
      .catch(async (err) => {
        // Only drop the stored session when the refresh token is genuinely dead
        // (SessionExpiredError). Transient failures (network, 5xx) keep the
        // session so a later call can retry instead of forcing a re-login.
        if (err instanceof SessionExpiredError) {
          await dropSession();
        }
        throw err;
      })
      .finally(() => {
        inFlightRefresh = null;
      });
  }

  const pending = inFlightRefresh;
  try {
    const fresh = await pending;
    return fresh.idToken;
  } catch (err) {
    // The refresh token is dead (24h cap / revoked consent). Last chance before
    // bothering the user: mint a fresh one off the Entra browser session.
    if (err instanceof SessionExpiredError) {
      const revived = await silentLogin();
      if (revived) return revived.idToken;
      // Silent renewal is out of options — now the UI may say "signed out".
      notifySessionChanged(null);
    }
    throw err;
  }
}

let inFlightRenew: Promise<string | null> | null = null;

/**
 * Jeton NEUF, même si l'actuel n'a pas expiré. Pour le cas où le backend
 * refuse un jeton que nous croyons valide (06.10.2026 : `/servicenow/search` en
 * 502 parce que l'annuaire EPFL ne reconnaissait pas l'usager à partir de son
 * id_token — « pour '' » ; se déconnecter puis se reconnecter réglait tout).
 *
 * D'abord une reconnexion silencieuse (même effet qu'un « déco-reco », sans
 * fenêtre, sur la session Entra du navigateur), qui émet un jeton complet ;
 * à défaut, le refresh_token. Null si les deux échouent : c'est alors à
 * l'usager de se reconnecter. Les appels concurrents partagent le même
 * renouvellement (le refresh_token n'est utilisable qu'une fois).
 */
export async function renewAuthToken(): Promise<string | null> {
  if (inFlightRenew) return inFlightRenew;
  inFlightRenew = (async () => {
    const revived = await silentLogin();
    if (revived) return revived.idToken;
    const session = await getCurrentSession();
    if (!session) return null;
    try {
      const fresh = await refreshTokens(session.refreshToken);
      await saveSession(fresh);
      return fresh.idToken;
    } catch {
      return null;
    }
  })().finally(() => {
    inFlightRenew = null;
  });
  return inFlightRenew;
}
